<template>
  <div class="card mt-4 people-groups-report">
    <div
      class="card-header pb-0 report-header"
      role="button"
      tabindex="0"
      :aria-expanded="expanded"
      aria-controls="people-groups-report-body"
      @click="toggleExpanded"
      @keydown.enter.prevent="toggleExpanded"
      @keydown.space.prevent="toggleExpanded"
    >
      <div class="d-flex justify-content-between align-items-center">
        <div>
          <h6 class="mb-0">People Groups Report</h6>
          <p class="text-sm text-secondary mb-0">
            Group adoption, membership and invitation activity across active organizations
          </p>
        </div>
        <div class="d-flex align-items-center gap-2">
          <span v-if="report && !expanded" class="text-xs text-secondary">
            {{ formatNumber(summary.people_group_count) }} groups ·
            {{ formatNumber(invitations.pending) }} pending invites
          </span>
          <i class="material-icons text-secondary align-middle">{{ expanded ? "expand_less" : "expand_more" }}</i>
        </div>
      </div>
    </div>

    <div v-if="expanded" id="people-groups-report-body" class="card-body">
      <div class="report-filters">
        <OrganizationMultiSelect
          v-model="selectedOrganizationIds"
          :options="filterOptions"
          :all-label="includeInactive ? 'All organizations' : 'All active organizations'"
        />
        <label class="form-check report-toggle mb-0">
          <input v-model="includeInactive" type="checkbox" class="form-check-input">
          <span class="form-check-label">Include inactive organizations &amp; locations</span>
        </label>
        <button type="button" class="btn btn-primary btn-sm mb-0" :disabled="loading" @click="load">
          <i class="material-icons align-middle me-1" style="font-size:16px;">refresh</i>
          {{ loading ? "Refreshing..." : "Refresh" }}
        </button>
        <span v-if="report?.generated_at" class="text-xs text-secondary ms-auto">
          Updated {{ formatTimestamp(report.generated_at) }}
        </span>
      </div>

      <div v-if="errorMessage" class="alert alert-danger d-flex justify-content-between align-items-center gap-3" role="alert">
        <span>{{ errorMessage }}</span>
        <button type="button" class="btn btn-sm btn-outline-danger mb-0" :disabled="loading" @click="load">Try again</button>
      </div>
      <div v-else-if="loading && !report" class="report-state" role="status">
        <span class="spinner-border spinner-border-sm text-success" aria-hidden="true"></span>
        Gathering People Group activity...
      </div>

      <template v-else-if="report">
        <p class="text-xs text-secondary mb-3">
          Showing {{ formatNumber(report.scope?.organization_count) }} organization{{ report.scope?.organization_count === 1 ? "" : "s" }}
          and {{ formatNumber(report.scope?.location_count) }} location{{ report.scope?.location_count === 1 ? "" : "s" }}
          <template v-if="!includeInactive">(active only)</template>.
        </p>

        <section class="metric-grid" aria-label="People Group summary">
          <article class="metric metric--blue">
            <span class="metric-label">People groups</span>
            <strong>{{ formatNumber(summary.people_group_count) }}</strong>
            <small>{{ formatNumber(summary.groups_created_last_30_days) }} created in the last 30 days</small>
          </article>
          <article class="metric metric--green">
            <span class="metric-label">Groups with 2+ members</span>
            <strong>{{ formatNumber(summary.groups_with_multiple_members) }}</strong>
            <small>{{ formatPercent(summary.multiple_member_percentage) }} of groups · {{ formatNumber(summary.groups_with_single_member) }} with one member</small>
          </article>
          <article class="metric metric--violet">
            <span class="metric-label">Invites sent</span>
            <strong>{{ formatNumber(invitations.sent) }}</strong>
            <small>{{ formatNumber(invitations.sent_last_30_days) }} in the last 30 days</small>
          </article>
          <article class="metric metric--amber">
            <span class="metric-label">Invites pending</span>
            <strong>{{ formatNumber(invitations.pending) }}</strong>
            <small>{{ formatNumber(invitations.expired) }} more expired without a response</small>
          </article>
        </section>

        <div class="report-grid">
          <section class="report-panel">
            <h6>Invitation outcomes</h6>
            <ul class="stat-list">
              <li><span>Accepted</span><strong>{{ formatNumber(invitations.accepted) }}</strong></li>
              <li><span>Declined</span><strong>{{ formatNumber(invitations.declined) }}</strong></li>
              <li><span>Cancelled</span><strong>{{ formatNumber(invitations.cancelled) }}</strong></li>
              <li><span>Expired</span><strong>{{ formatNumber(invitations.expired) }}</strong></li>
              <li><span>Email not delivered</span><strong>{{ formatNumber(invitations.failed) }}</strong></li>
              <li><span>Acceptance rate</span><strong>{{ formatPercent(invitations.acceptance_rate) }}</strong></li>
            </ul>
            <p class="text-xs text-secondary mb-0">Sent excludes invitations whose email failed to send. Pending excludes expired invitations.</p>
          </section>

          <section class="report-panel">
            <h6>Membership</h6>
            <ul class="stat-list">
              <li><span>Unique members</span><strong>{{ formatNumber(summary.unique_members) }}</strong></li>
              <li><span>Total memberships</span><strong>{{ formatNumber(summary.total_memberships) }}</strong></li>
              <li><span>Average group size</span><strong>{{ summary.average_group_size ?? "—" }}</strong></li>
              <li><span>Largest group</span><strong>{{ formatNumber(summary.largest_group_size) }}</strong></li>
              <li><span>Groups at member limit</span><strong>{{ formatNumber(summary.groups_at_capacity) }}</strong></li>
              <li><span>Members allowing admin management</span><strong>{{ formatNumber(summary.members_allowing_admin_management) }}</strong></li>
            </ul>
          </section>

          <section class="report-panel">
            <h6>Adoption</h6>
            <ul class="stat-list">
              <li><span>Locations using People Groups</span><strong>{{ formatNumber(summary.locations_with_groups) }} / {{ formatNumber(report.scope?.location_count) }}</strong></li>
              <li><span>Location adoption</span><strong>{{ formatPercent(summary.location_adoption_percentage) }}</strong></li>
              <li><span>Organizations using People Groups</span><strong>{{ formatNumber(summary.organizations_with_groups) }} / {{ formatNumber(report.scope?.organization_count) }}</strong></li>
              <li><span>Group audit events (30 days)</span><strong>{{ formatNumber(summary.audit_events_last_30_days) }}</strong></li>
            </ul>
          </section>
        </div>

        <div class="report-grid report-grid--charts">
          <section class="report-panel">
            <h6>Groups by size</h6>
            <div v-for="bucket in report.size_distribution" :key="bucket.label" class="bar-row">
              <span class="bar-label">{{ bucket.label }}</span>
              <div class="bar-track"><div class="bar-fill" :style="{ width: barWidth(bucket.groups, maxSizeGroups) }"></div></div>
              <span class="bar-value">{{ formatNumber(bucket.groups) }}</span>
            </div>
          </section>

          <section class="report-panel">
            <h6>Groups by type</h6>
            <div v-for="type in report.by_type" :key="type.people_group_type" class="bar-row">
              <span class="bar-label text-capitalize">{{ type.people_group_type }}</span>
              <div class="bar-track"><div class="bar-fill bar-fill--green" :style="{ width: barWidth(type.groups, maxTypeGroups) }"></div></div>
              <span class="bar-value">{{ formatNumber(type.groups) }}</span>
            </div>
            <p class="text-xs text-secondary mt-2 mb-0">
              2+ members: <template v-for="(type, index) in report.by_type" :key="type.people_group_type">
                <span class="text-capitalize">{{ type.people_group_type }}</span> {{ formatNumber(type.groups_with_multiple_members) }}<span v-if="index < report.by_type.length - 1"> · </span>
              </template>
            </p>
          </section>

          <section class="report-panel">
            <h6>Groups created (last 12 months)</h6>
            <div class="month-chart" role="img" aria-label="People Groups created per month">
              <div v-for="month in report.monthly_groups" :key="month.month" class="month-column" :title="`${month.month}: ${month.groups}`">
                <span class="month-value">{{ month.groups || "" }}</span>
                <div class="month-track"><div class="month-bar" :style="{ height: barWidth(month.groups, maxMonthlyGroups, 4) }"></div></div>
                <small>{{ monthLabel(month.month) }}</small>
              </div>
            </div>
          </section>
        </div>

        <section class="report-panel mb-3">
          <h6>Largest groups</h6>
          <div v-if="report.largest_groups?.length" class="table-responsive">
            <table class="table table-sm align-middle report-table mb-0">
              <thead>
                <tr><th>Group</th><th>Type</th><th>Members</th><th>Organization / location</th><th>Created</th></tr>
              </thead>
              <tbody>
                <tr v-for="group in report.largest_groups" :key="group.id">
                  <td class="fw-semibold">{{ group.name }}</td>
                  <td class="text-capitalize">{{ group.people_group_type }}</td>
                  <td>{{ formatNumber(group.member_count) }}</td>
                  <td>{{ group.organization_name }} <span class="text-secondary">· {{ group.location_name }}</span></td>
                  <td>{{ formatDate(group.created_at) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p v-else class="text-sm text-secondary mb-0">No People Groups exist in this scope yet.</p>
        </section>

        <section class="report-panel">
          <div class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
            <h6 class="mb-0">By organization and location</h6>
            <input
              v-model.trim="organizationSearch"
              type="search"
              class="form-control form-control-sm search-input"
              placeholder="Search organization or location"
              aria-label="Search organizations and locations"
            >
          </div>
          <div v-if="visibleOrganizations.length" class="table-responsive org-table-wrap">
            <table class="table table-sm align-middle report-table mb-0">
              <thead>
                <tr>
                  <th class="expand-col"></th>
                  <th>Organization / location</th>
                  <th>Groups</th>
                  <th>2+ members</th>
                  <th>Members</th>
                  <th>Invites sent</th>
                  <th>Pending</th>
                </tr>
              </thead>
              <tbody>
                <template v-for="organization in visibleOrganizations" :key="organization.organization_id">
                  <tr>
                    <td class="expand-col">
                      <button
                        type="button"
                        class="expand-button"
                        :disabled="!organization.locations.length"
                        :aria-expanded="isExpanded(organization)"
                        :aria-label="`${isExpanded(organization) ? 'Hide' : 'Show'} locations for ${organization.organization_name}`"
                        @click="toggleOrganization(organization)"
                      >
                        <i class="material-icons">{{ isExpanded(organization) ? "expand_more" : "chevron_right" }}</i>
                      </button>
                    </td>
                    <td class="fw-semibold">
                      {{ organization.organization_name }}
                      <span v-if="organization.status !== 'active'" class="badge bg-secondary ms-1">{{ organization.status }}</span>
                    </td>
                    <td>{{ formatNumber(organization.people_group_count) }}</td>
                    <td>{{ formatNumber(organization.groups_with_multiple_members) }}</td>
                    <td>{{ formatNumber(organization.unique_members) }}</td>
                    <td>{{ formatNumber(organization.invitations_sent) }}</td>
                    <td>{{ formatNumber(organization.invitations_pending) }}</td>
                  </tr>
                  <template v-if="isExpanded(organization)">
                    <tr v-for="location in visibleLocations(organization)" :key="`location-${location.location_id}`" class="location-row">
                      <td class="expand-col"></td>
                      <td class="location-name">
                        {{ location.location_name }}
                        <span v-if="location.status !== 'active'" class="badge bg-secondary ms-1">{{ location.status }}</span>
                      </td>
                      <td>{{ formatNumber(location.people_group_count) }}</td>
                      <td>{{ formatNumber(location.groups_with_multiple_members) }}</td>
                      <td>{{ formatNumber(location.unique_members) }}</td>
                      <td>{{ formatNumber(location.invitations_sent) }}</td>
                      <td>{{ formatNumber(location.invitations_pending) }}</td>
                    </tr>
                  </template>
                </template>
              </tbody>
            </table>
          </div>
          <p v-else class="text-sm text-secondary mb-0">
            {{ organizationSearch ? "No organizations or locations match your search." : "No organizations in this scope have People Groups or invitations yet." }}
          </p>
        </section>
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from "vue";
import api from "@/config/api";
import OrganizationMultiSelect from "@/components/Admin/OrganizationMultiSelect.vue";

const expanded = ref(false);
const hasLoadedOnce = ref(false);
const loading = ref(false);
const errorMessage = ref("");
const report = ref(null);
const selectedOrganizationIds = ref([]);
const includeInactive = ref(false);
const filterOptionsSource = ref([]);
const organizationSearch = ref("");
const expandedOrganizations = ref(new Set());
let loadSequence = 0;
let filterTimer = null;

const summary = computed(() => report.value?.summary || {});
const invitations = computed(() => report.value?.invitations || {});

// Inactive organizations are only selectable once they are included in the report.
const filterOptions = computed(() => includeInactive.value
  ? filterOptionsSource.value
  : filterOptionsSource.value.filter((option) => option.status === "active"));

const maxSizeGroups = computed(() => Math.max(1, ...(report.value?.size_distribution || []).map((bucket) => bucket.groups)));
const maxTypeGroups = computed(() => Math.max(1, ...(report.value?.by_type || []).map((type) => type.groups)));
const maxMonthlyGroups = computed(() => Math.max(1, ...(report.value?.monthly_groups || []).map((month) => month.groups)));

function matchesSearch(value) {
  return String(value || "").toLowerCase().includes(organizationSearch.value.toLowerCase());
}

function matchingLocations(organization) {
  if (!organizationSearch.value) return organization.locations;
  return organization.locations.filter((location) => matchesSearch(location.location_name));
}

const visibleOrganizations = computed(() => (report.value?.organizations || []).filter((organization) =>
  !organizationSearch.value
  || matchesSearch(organization.organization_name)
  || matchingLocations(organization).length > 0
));

// A search that only matches a location opens its organization so the match is visible.
function isExpanded(organization) {
  if (!organization.locations.length) return false;
  if (expandedOrganizations.value.has(organization.organization_id)) return true;
  return Boolean(organizationSearch.value) && !matchesSearch(organization.organization_name);
}

function visibleLocations(organization) {
  return matchesSearch(organization.organization_name) ? organization.locations : matchingLocations(organization);
}

function toggleOrganization(organization) {
  const next = new Set(expandedOrganizations.value);
  if (next.has(organization.organization_id)) {
    next.delete(organization.organization_id);
  } else {
    next.add(organization.organization_id);
  }
  expandedOrganizations.value = next;
}

// Details are fetched the first time the card is opened, never while it is minimized.
function toggleExpanded() {
  expanded.value = !expanded.value;
  if (expanded.value && !hasLoadedOnce.value) {
    hasLoadedOnce.value = true;
    load();
  }
}

async function load() {
  const sequence = ++loadSequence;
  loading.value = true;
  errorMessage.value = "";
  try {
    const params = { include_inactive: includeInactive.value ? 1 : 0 };
    if (selectedOrganizationIds.value.length) {
      params.organization_ids = selectedOrganizationIds.value;
    }
    const response = await api.get("/admin/people-groups-report", { params });
    if (sequence !== loadSequence) return;
    if (!response.data?.data) {
      throw new Error("The People Groups report response was empty.");
    }
    report.value = response.data.data;
    filterOptionsSource.value = response.data.data.organization_options || [];
  } catch (error) {
    if (sequence !== loadSequence) return;
    console.error("Error fetching People Groups report:", error);
    errorMessage.value = error?.response?.data?.message || error.message || "The People Groups report could not be loaded.";
  } finally {
    if (sequence === loadSequence) loading.value = false;
  }
}

// Filter changes reload the report once the selection settles.
watch([selectedOrganizationIds, includeInactive], () => {
  if (!includeInactive.value) {
    const activeIds = new Set(filterOptionsSource.value.filter((option) => option.status === "active").map((option) => option.organization_id));
    const kept = selectedOrganizationIds.value.filter((id) => activeIds.has(id));
    if (kept.length !== selectedOrganizationIds.value.length) {
      selectedOrganizationIds.value = kept;
      return;
    }
  }
  clearTimeout(filterTimer);
  expandedOrganizations.value = new Set();
  if (!hasLoadedOnce.value) return;
  filterTimer = setTimeout(load, 500);
});

onBeforeUnmount(() => {
  clearTimeout(filterTimer);
  loadSequence++;
});

function formatNumber(value) {
  return new Intl.NumberFormat().format(Number(value) || 0);
}

function formatPercent(value) {
  return value === null || value === undefined ? "—" : `${value}%`;
}

function formatTimestamp(value) {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
}

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(String(value).replace(" ", "T"));
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleDateString();
}

function monthLabel(month) {
  const date = new Date(`${month}-01T00:00:00`);
  return Number.isNaN(date.getTime()) ? month : date.toLocaleDateString(undefined, { month: "short" });
}

function barWidth(value, max, minPercent = 2) {
  const numeric = Number(value) || 0;
  if (!numeric) return "0%";
  return `${Math.max(minPercent, (numeric / max) * 100)}%`;
}
</script>

<style scoped>
.report-header {
  cursor: pointer;
}

.report-filters {
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
}

.report-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 6px;
  font-size: 0.8rem;
}

.report-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 120px;
  color: #475569;
  font-size: 0.85rem;
}

.metric-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}

.metric {
  padding: 14px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  background: #fff;
}

.metric-label,
.metric small {
  display: block;
  color: #64748b;
  font-size: 0.72rem;
}

.metric strong {
  display: block;
  margin: 6px 0 4px;
  color: #0f172a;
  font-size: 1.8rem;
  font-weight: 800;
  line-height: 1.1;
}

.metric--blue { border-top: 3px solid #0ea5e9; }
.metric--green { border-top: 3px solid #22c55e; }
.metric--violet { border-top: 3px solid #8b5cf6; }
.metric--amber { border-top: 3px solid #f59e0b; }

.report-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 12px;
}

.report-panel {
  min-width: 0;
  padding: 14px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  background: #fff;
}

.report-panel h6 {
  margin-bottom: 10px;
  font-size: 0.85rem;
}

.stat-list {
  margin: 0 0 8px;
  padding: 0;
  list-style: none;
}

.stat-list li {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 5px 0;
  border-bottom: 1px solid #f1f5f9;
  font-size: 0.8rem;
}

.stat-list li span {
  color: #64748b;
}

.bar-row {
  display: grid;
  grid-template-columns: 92px minmax(0, 1fr) 40px;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  font-size: 0.75rem;
}

.bar-label {
  color: #475569;
}

.bar-track {
  height: 10px;
  overflow: hidden;
  border-radius: 999px;
  background: #f1f5f9;
}

.bar-fill {
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #38bdf8, #0284c7);
}

.bar-fill--green {
  background: linear-gradient(90deg, #34d399, #059669);
}

.bar-value {
  text-align: right;
  font-weight: 700;
}

.month-chart {
  display: flex;
  align-items: flex-end;
  gap: 4px;
  height: 140px;
}

.month-column {
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  height: 100%;
  min-width: 0;
}

.month-value {
  height: 14px;
  color: #475569;
  font-size: 0.62rem;
  font-weight: 700;
}

.month-track {
  display: flex;
  flex: 1;
  align-items: flex-end;
  width: 100%;
}

.month-bar {
  width: 100%;
  min-height: 0;
  border-radius: 4px 4px 0 0;
  background: linear-gradient(180deg, #38bdf8, #0284c7);
}

.month-column small {
  color: #64748b;
  font-size: 0.62rem;
}

.report-table {
  font-size: 0.78rem;
}

.report-table thead th {
  color: #64748b;
  font-size: 0.66rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
}

.org-table-wrap {
  max-height: 420px;
  overflow: auto;
}

.org-table-wrap thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #f8fafc;
}

.search-input {
  max-width: 280px;
}

.expand-col {
  width: 36px;
  padding-right: 0 !important;
}

.expand-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  padding: 0;
  border: 0;
  border-radius: 6px;
  color: #475569;
  background: transparent;
}

.expand-button:hover:not(:disabled) { background: #e2e8f0; }
.expand-button:disabled { opacity: 0.25; cursor: default; }
.expand-button .material-icons { font-size: 20px; }

.location-row td { background: #f8fafc; }
.location-name { padding-left: 24px !important; }

@media (max-width: 992px) {
  .metric-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .report-grid {
    grid-template-columns: 1fr;
  }
}
</style>
